import React, { useEffect, useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import { cn } from '../../lib/utils'

const LOCAL_STORAGE_KEY = 'karaoke-emoji-tray'
const DEFAULT_EMOJIS = ['👏🏼', '🔥', '🎉', '💯', '🫶']
const MAX_EMOJIS = 12
// eslint-disable-next-line no-misleading-character-class
const EMOJI_SEQUENCE_REGEX = /^([\p{Extended_Pictographic}\p{Emoji_Presentation}](?:[\p{Emoji_Component}\u200D\uFE0F])*)+$/u

const readStoredEmojis = () => {
  if (typeof window === 'undefined') {
    return [...DEFAULT_EMOJIS]
  }

  try {
    const stored = window.localStorage.getItem(LOCAL_STORAGE_KEY)
    if (!stored) {
      return [...DEFAULT_EMOJIS]
    }

    const parsed = JSON.parse(stored)
    if (!Array.isArray(parsed)) {
      return [...DEFAULT_EMOJIS]
    }

    const unique = Array.from(
      new Set(
        parsed
          .map((emoji) => (typeof emoji === 'string' ? emoji.trim() : ''))
          .filter((emoji) => emoji && EMOJI_SEQUENCE_REGEX.test(emoji))
      )
    )

    return unique.length > 0 ? unique.slice(0, MAX_EMOJIS) : [...DEFAULT_EMOJIS]
  } catch (error) {
    console.warn('Failed to read emoji tray:', error)
    return [...DEFAULT_EMOJIS]
  }
}

const EmojiSheetB = ({ onSendEmoji, onClose, dragHandleProps = {} }) => {
  const [emojis, setEmojis] = useState(readStoredEmojis)
  const [isAdding, setIsAdding] = useState(false)
  const [newEmoji, setNewEmoji] = useState('')
  const [error, setError] = useState('')
  const [pendingEmoji, setPendingEmoji] = useState('')

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    try {
      window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(emojis))
    } catch (persistError) {
      console.warn('Failed to persist emoji tray:', persistError)
    }
  }, [emojis])

  const canAddMore = emojis.length < MAX_EMOJIS

  const orderedEmojis = useMemo(() => emojis.slice(0, MAX_EMOJIS), [emojis])

  const sendEmoji = async (emoji) => {
    if (typeof onSendEmoji !== 'function') return

    try {
      setPendingEmoji(emoji)
      await onSendEmoji(emoji)
    } finally {
      setPendingEmoji('')
    }
  }

  const addEmoji = (event) => {
    event.preventDefault()

    const value = newEmoji.trim()
    if (!value) {
      setError('Enter an emoji')
      return
    }

    if (!EMOJI_SEQUENCE_REGEX.test(value)) {
      setError('Only emoji characters are allowed')
      return
    }

    if (orderedEmojis.includes(value)) {
      setError('Already in your emoji set')
      return
    }

    if (!canAddMore) {
      setError('Emoji limit reached')
      return
    }

    setEmojis((previous) => [...previous, value])
    setNewEmoji('')
    setError('')
    setIsAdding(false)
  }

  return (
    <section
      className="room-elevated room-glow room-content-safe-inline mx-auto w-full max-w-lg rounded-t-3xl border-b-0 pb-4 pt-3 text-[var(--room-text)]"
      aria-label="Emoji reactions sheet"
    >
      <button
        type="button"
        aria-label="Drag to dismiss utility sheet"
        className="mx-auto mb-3 flex h-6 w-16 touch-none items-center justify-center rounded-full"
        style={{ touchAction: 'none' }}
        {...dragHandleProps}
      >
        <span className="h-1.5 w-12 rounded-full bg-white/20" />
      </button>
      <div className="mb-4">
        <h3 className="text-base font-semibold">Emoji Shower</h3>
      </div>

      <div className="grid grid-cols-5 gap-2">
        {orderedEmojis.map((emoji) => {
          const isPending = pendingEmoji === emoji
          return (
            <button
              key={emoji}
              type="button"
              onClick={() => sendEmoji(emoji)}
              disabled={Boolean(pendingEmoji) && !isPending}
              className={cn(
                'room-focus-ring h-12 rounded-xl border text-2xl transition-transform',
                isPending
                  ? 'scale-95 border-[color:var(--room-primary)] bg-[color:rgba(46,168,255,0.22)]'
                  : 'border-white/15 bg-white/5 hover:-translate-y-0.5 hover:bg-white/10 active:scale-95'
              )}
              aria-label={`Send ${emoji}`}
            >
              {emoji}
            </button>
          )
        })}

        <button
          type="button"
          onClick={() => {
            if (!canAddMore) return
            setIsAdding((open) => !open)
            setError('')
          }}
          disabled={!canAddMore}
          className={cn(
            'room-focus-ring h-12 rounded-xl border text-lg transition-colors',
            canAddMore
              ? 'border-dashed border-white/25 bg-white/5 text-[var(--room-muted)] hover:bg-white/10 hover:text-[var(--room-text)]'
              : 'cursor-not-allowed border-white/10 bg-black/20 text-white/35'
          )}
          aria-label="Add emoji"
        >
          <Plus className="mx-auto h-5 w-5" />
        </button>
      </div>

      {isAdding ? (
        <form onSubmit={addEmoji} className="mt-3 space-y-2">
          <div className="flex items-center gap-2">
            <input
              type="text"
              maxLength={8}
              value={newEmoji}
              onChange={(event) => {
                setNewEmoji(event.target.value)
                setError('')
              }}
              className="room-input room-focus-ring h-11 flex-1 rounded-xl px-3 text-base"
              placeholder="Type or paste emoji"
              aria-label="New emoji"
            />
            <button
              type="submit"
              className="room-focus-ring h-11 rounded-xl border border-[color:var(--room-primary)] bg-[color:rgba(46,168,255,0.22)] px-4 text-sm font-semibold text-[var(--room-text)]"
            >
              Add
            </button>
          </div>
          {error ? <p className="text-xs text-[var(--room-danger)]">{error}</p> : null}
        </form>
      ) : null}
    </section>
  )
}

export default EmojiSheetB
